export type WidgetOpenState = 'open' | 'closed';

// Two states that used to collapse onto a single 'disconnected' value are
// now distinct, because they mean different things and call for different
// UI (see the design integration notes on the connection-status fix):
//   - 'creationFailed': the very first createConversation() call itself
//     failed. There is no conversation yet, so there is nothing to
//     "reconnect" — only connectToConversation() retrying from scratch
//     (the message list's own "Try again") makes sense here.
//   - 'reconnecting': a conversation already exists and its WebSocket
//     dropped. The conversation and its history are kept; only the
//     realtime channel is being re-established (automatically, with the
//     underlying WebSocketClient's own backoff — see client/websocket.ts).
export type WidgetConnectionStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'creationFailed';

export interface WidgetState {
  openState: WidgetOpenState;
  connectionStatus: WidgetConnectionStatus;
  conversationId: string | null;
  // Who claimed this ticket in Discord — session-only like connectionStatus,
  // not persisted: always re-derived from the next createConversation()
  // response or a live 'ticket_claimed' push, never stale localStorage.
  assignedStaffName: string | null;
  // The claiming staff member's Discord avatar, captured from the same
  // 'ticket_claimed' push — session-only, same reasoning as
  // assignedStaffName. Lets the greeting teaser show the real person.
  assignedStaffAvatarUrl: string | null;
  // Staff messages that arrived while the customer wasn't looking (panel
  // closed, tab hidden, or scrolled away from the latest messages).
  // Persisted so a refresh or in-site navigation keeps the badge — only
  // actually *seeing* the latest messages clears it.
  unreadCount: number;
  // Which past conversations have a reply the customer never actually
  // viewed — distinct from unreadCount (a single number for "right now"):
  // this survives the conversation ending, so the history list can still
  // flag it instead of the fact silently disappearing along with the
  // badge. Cleared per-id the moment that conversation is actually viewed
  // (live or in the read-only history viewer). Persisted.
  unreadConversationIds: string[];
  // The notification-sound preference (🔊 toggle in the overflow menu).
  // Persisted; defaults to on.
  soundEnabled: boolean;
  // The browser-notification preference (🔔 toggle in the overflow menu) —
  // distinct from the OS-level Notification.permission grant, which is
  // never stored here (always re-read live from the browser). This is a
  // separate, in-widget "do I want them" switch on top of that grant.
  // Persisted; defaults to on.
  browserNotificationsEnabled: boolean;
}

export interface WidgetStateStore {
  getState(): WidgetState;
  setOpenState(openState: WidgetOpenState): void;
  setConnectionStatus(connectionStatus: WidgetConnectionStatus): void;
  setConversationId(conversationId: string | null): void;
  setAssignedStaffName(assignedStaffName: string | null): void;
  setAssignedStaffAvatarUrl(assignedStaffAvatarUrl: string | null): void;
  setUnreadCount(unreadCount: number): void;
  isConversationUnread(conversationId: string): boolean;
  markConversationUnread(conversationId: string): void;
  markConversationRead(conversationId: string): void;
  setSoundEnabled(soundEnabled: boolean): void;
  setBrowserNotificationsEnabled(browserNotificationsEnabled: boolean): void;
  // The greeting teaser's "dismissed via ×" flag lives in sessionStorage,
  // not localStorage: it must reset for a genuinely new browsing session
  // (so the teaser can greet again) while surviving refreshes and in-site
  // navigation within the current one.
  isTeaserDismissed(): boolean;
  setTeaserDismissed(): void;
}

const TEASER_DISMISSED_KEY = 'flix-chat-widget-teaser-dismissed';

const STORAGE_KEY = 'flix-chat-widget-state';

// Only open/closed and the conversationId are persisted — never message
// history or attachments. Both are read from the existing realtime pipeline
// on load, not from storage.
interface PersistedWidgetState {
  openState: WidgetOpenState;
  conversationId: string | null;
  unreadCount: number;
  unreadConversationIds: string[];
  soundEnabled: boolean;
  browserNotificationsEnabled: boolean;
}

// A generous but finite cap — this is a small "recent unread" memory, not an
// archive; nothing prunes it otherwise (a conversationId only ever leaves the
// list by actually being viewed), so without a cap a customer who never
// opens their history would grow this indefinitely.
const MAX_TRACKED_UNREAD_CONVERSATIONS = 50;

function loadPersistedState(): PersistedWidgetState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<PersistedWidgetState>;

    if (parsed.openState !== 'open' && parsed.openState !== 'closed') {
      return null;
    }

    return {
      openState: parsed.openState,
      conversationId: typeof parsed.conversationId === 'string' ? parsed.conversationId : null,
      unreadCount:
        typeof parsed.unreadCount === 'number' && Number.isInteger(parsed.unreadCount) && parsed.unreadCount > 0
          ? parsed.unreadCount
          : 0,
      unreadConversationIds: Array.isArray(parsed.unreadConversationIds)
        ? parsed.unreadConversationIds.filter((id): id is string => typeof id === 'string')
        : [],
      soundEnabled: parsed.soundEnabled !== false,
      browserNotificationsEnabled: parsed.browserNotificationsEnabled !== false,
    };
  } catch {
    return null;
  }
}

function savePersistedState(state: PersistedWidgetState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Persistence is best-effort (private browsing, storage disabled, etc.).
  }
}

export function createWidgetState(): WidgetStateStore {
  const persisted = loadPersistedState();

  let state: WidgetState = {
    // The open/closed state only carries across page loads while a
    // conversation is actually on record — mid-conversation navigation and
    // refreshes restore the panel exactly as it was, but without one the
    // widget always starts closed: a visitor who left the empty panel open
    // on some earlier page shouldn't have it spring open on every
    // subsequent one.
    openState: persisted?.conversationId ? persisted.openState : 'closed',
    connectionStatus: 'idle',
    conversationId: persisted?.conversationId ?? null,
    assignedStaffName: null,
    assignedStaffAvatarUrl: null,
    unreadCount: persisted?.unreadCount ?? 0,
    unreadConversationIds: persisted?.unreadConversationIds ?? [],
    soundEnabled: persisted?.soundEnabled ?? true,
    browserNotificationsEnabled: persisted?.browserNotificationsEnabled ?? true,
  };

  function persist(): void {
    savePersistedState({
      openState: state.openState,
      conversationId: state.conversationId,
      unreadCount: state.unreadCount,
      unreadConversationIds: state.unreadConversationIds,
      soundEnabled: state.soundEnabled,
      browserNotificationsEnabled: state.browserNotificationsEnabled,
    });
  }

  return {
    getState(): WidgetState {
      return state;
    },

    setOpenState(openState: WidgetOpenState): void {
      state = { ...state, openState };
      persist();
    },

    setConnectionStatus(connectionStatus: WidgetConnectionStatus): void {
      state = { ...state, connectionStatus };
    },

    setConversationId(conversationId: string | null): void {
      state = { ...state, conversationId };
      persist();
    },

    setAssignedStaffName(assignedStaffName: string | null): void {
      state = { ...state, assignedStaffName };
    },

    setAssignedStaffAvatarUrl(assignedStaffAvatarUrl: string | null): void {
      state = { ...state, assignedStaffAvatarUrl };
    },

    setUnreadCount(unreadCount: number): void {
      state = { ...state, unreadCount: Math.max(0, unreadCount) };
      persist();
    },

    isConversationUnread(conversationId: string): boolean {
      return state.unreadConversationIds.includes(conversationId);
    },

    markConversationUnread(conversationId: string): void {
      if (state.unreadConversationIds.includes(conversationId)) {
        return;
      }

      // Oldest-first cap: a conversation that's been sitting unread the
      // longest is the least likely one the customer still cares about
      // finding flagged.
      const next = [...state.unreadConversationIds, conversationId].slice(-MAX_TRACKED_UNREAD_CONVERSATIONS);
      state = { ...state, unreadConversationIds: next };
      persist();
    },

    markConversationRead(conversationId: string): void {
      if (!state.unreadConversationIds.includes(conversationId)) {
        return;
      }

      state = {
        ...state,
        unreadConversationIds: state.unreadConversationIds.filter((id) => id !== conversationId),
      };
      persist();
    },

    setSoundEnabled(soundEnabled: boolean): void {
      state = { ...state, soundEnabled };
      persist();
    },

    setBrowserNotificationsEnabled(browserNotificationsEnabled: boolean): void {
      state = { ...state, browserNotificationsEnabled };
      persist();
    },

    isTeaserDismissed(): boolean {
      try {
        return sessionStorage.getItem(TEASER_DISMISSED_KEY) === '1';
      } catch {
        // sessionStorage unavailable (private mode, disabled) — treat as
        // not dismissed; the in-session guard in widget.ts still prevents
        // re-showing after a dismiss within the same page load.
        return false;
      }
    },

    setTeaserDismissed(): void {
      try {
        sessionStorage.setItem(TEASER_DISMISSED_KEY, '1');
      } catch {
        // Best-effort, same reasoning as savePersistedState.
      }
    },
  };
}
